import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "@mui/material";
import { MoreVertical } from "lucide-react";

import { Button } from "components/ui/Button";
import { useClickOutside } from "components/ui/DataTable/hooks/useClickOutside";
import { useDropdownPosition } from "components/ui/DataTable/hooks/useDropdownPosition";

import styles from "./DataTableRowActions.module.css";

export const DataTableRowActions = ({
  actions = [],
  row,
  onAction,
  ariaLabel = "Row actions",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const { position, updatePosition } = useDropdownPosition(buttonRef, isOpen, {
    align: "right",
    menuWidth: 180,
    menuRef,
  });

  useClickOutside(isOpen, buttonRef, menuRef, () => setIsOpen(false));

  if (!actions.length) return null;

  const visibleActions = actions.filter(
    (a) => !a.visibleWhen || a.visibleWhen(row)
  );
  const primaryActions = visibleActions.filter((a) => a.primary);
  const menuActions = visibleActions.filter(
    (a) => !a.primaryOnly && !a.primary
  );

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    onAction?.(action, row);
    setIsOpen(false);
  };

  return (
    <div className={styles.rowActionsCell} data-row-actions>
      {primaryActions.map((action, i) => {
        const Icon = action.icon;
        const isDisabled = typeof action.disabled === "function" ? action.disabled(row) : !!action.disabled;
        const tooltipTitle =
          (typeof action.tooltip === "function"
            ? action.tooltip(row)
            : action.tooltip) ||
          action.label ||
          "Action";
        if (Icon) {
          return (
            <Tooltip title={tooltipTitle} key={`primary-${i}`}>
              <span>
                <button
                  className={`${styles.rowActionsButton} ${isDisabled ? styles.rowActionsButtonDisabled : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) onAction?.(action, row);
                  }}
                  aria-label={action.label || "Action"}
                  disabled={isDisabled}
                >
                  <Icon size={18} />
                </button>
              </span>
            </Tooltip>
          );
        }
        return (
          <Button
            key={`primary-${i}`}
            size="small"
            variant="primary"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) onAction?.(action, row);
            }}
          >
            {action.label}
          </Button>
        );
      })}
      {menuActions.length > 0 && (
        <Tooltip title={ariaLabel}>
          <button
            ref={buttonRef}
            className={`${styles.rowActionsButton} ${
              isOpen ? styles.active : ""
            }`}
            onClick={handleToggle}
            aria-label={ariaLabel}
          >
            <MoreVertical size={18} />
          </button>
        </Tooltip>
      )}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.rowActionsDropdown}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
            }}
          >
            {menuActions.map((action, i) => {
              const Icon = action.icon;
              const isDisabled =
                typeof action.disabled === "function"
                  ? action.disabled(row)
                  : !!action.disabled;
              const disabledReason =
                isDisabled &&
                (typeof action.disabledReason === "function"
                  ? action.disabledReason(row)
                  : action.disabledReason);

              const menuItem = (
                <button
                  className={`${styles.rowActionsItem} ${isDisabled ? styles.rowActionsItemDisabled : ""}`}
                  onClick={(e) => {
                    if (!isDisabled) handleActionClick(e, action);
                  }}
                  disabled={isDisabled}
                  {...(action.testId && { "data-testid": action.testId })}
                >
                  {Icon && (
                    <span className={styles.rowActionsIcon}>
                      <Icon size={16} />
                    </span>
                  )}
                  {action.label}
                </button>
              );

              if (isDisabled && disabledReason) {
                return (
                  <Tooltip key={i} title={disabledReason} placement="left">
                    <span>{menuItem}</span>
                  </Tooltip>
                );
              }

              return <React.Fragment key={i}>{menuItem}</React.Fragment>;
            })}
          </div>,
          document.body
        )}
    </div>
  );
};
